import { useEffect, useState, useCallback, useRef } from 'react';
import pageStatusService from 'services/pageStatusService';
import websocketService from 'services/websocketService';

interface PageStatus {
  page_name: string;
  student_completed: boolean;
  counselor_completed?: boolean;
  counselor_verified: boolean;
}

interface UsePageStatusProps {
  studentId: string | number;
  onPageStatusUpdate?: (pageStatus: PageStatus[]) => void;
}

export const usePageStatus = ({ studentId, onPageStatusUpdate }: UsePageStatusProps) => {
  const [pageStatus, setPageStatus] = useState<PageStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isListenerAttached = useRef(false);
  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onPageStatusUpdateRef = useRef(onPageStatusUpdate);
  const studentIdRef = useRef(studentId);

  // Keep refs updated
  useEffect(() => {
    onPageStatusUpdateRef.current = onPageStatusUpdate;
    studentIdRef.current = studentId;
  }, [onPageStatusUpdate, studentId]);

  // Fetch initial page status
  const fetchPageStatus = useCallback(async () => {
    const currentStudentId = studentIdRef.current;
    if (!currentStudentId) {
      setPageStatus([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const res: any = await pageStatusService.getPageStatus(currentStudentId);
      
      // Check if studentId changed during fetch
      if (studentIdRef.current !== currentStudentId) {
        return;
      }
      
      const formattedStatus =
        res && res.length > 0
          ? res.map((item: any) => ({
              page_name: item.page_name,
              student_completed: item.student_completed,
              counselor_completed: item.counselor_completed,
              counselor_verified: item.counselor_verified,
            }))
          : [];

      setPageStatus(formattedStatus);

      // Use ref to avoid stale closure
      if (onPageStatusUpdateRef.current) {
        onPageStatusUpdateRef.current(formattedStatus);
      }
    } catch (err) {
      // Check if studentId changed during error
      if (studentIdRef.current !== currentStudentId) {
        return;
      }
      setError('Failed to fetch page status');
      console.error('Error fetching page status:', err);
    } finally {
      // Only update loading state if studentId hasn't changed
      if (studentIdRef.current === currentStudentId) {
        setIsLoading(false);
      }
    }
  }, []); // Remove dependencies to prevent unnecessary re-creations

  // Handle page progress updates from websocket
  const handlePageProgressUpdate = useCallback((data: any) => {
    const currentStudentId = studentIdRef.current;
    
    // Verify the update is for the current student (if student_id is provided)
    // If student_id is not in data, we accept it (session-based updates)
    if (
      data &&
      (data.action === 'complete' || data.action === 'verify') &&
      data.page_name &&
      data.result &&
      (!data.student_id || String(data.student_id) === String(currentStudentId))
    ) {
      setPageStatus((prev: PageStatus[]) => {
        // Double-check studentId hasn't changed
        if (studentIdRef.current !== currentStudentId) {
          return prev;
        }

        const updatedStatus = [...prev];
        const existingPageIndex = updatedStatus.findIndex(
          (item) => item.page_name === data.page_name
        );

        if (existingPageIndex >= 0) {
          // Update existing page status - merge carefully to preserve existing values
          updatedStatus[existingPageIndex] = {
            ...updatedStatus[existingPageIndex],
            student_completed: data.result.student_completed !== undefined 
              ? data.result.student_completed 
              : updatedStatus[existingPageIndex].student_completed,
            counselor_completed: data.result.counselor_completed !== undefined
              ? data.result.counselor_completed
              : updatedStatus[existingPageIndex].counselor_completed,
            counselor_verified: data.result.counselor_verified !== undefined
              ? data.result.counselor_verified
              : updatedStatus[existingPageIndex].counselor_verified,
          };
        } else {
          // Add new page status if it doesn't exist
          updatedStatus.push({
            page_name: data.page_name,
            student_completed: data.result.student_completed ?? false,
            counselor_completed: data.result.counselor_completed ?? false,
            counselor_verified: data.result.counselor_verified ?? false,
          });
        }

        const finalStatus = updatedStatus;
        
        // Notify parent of update using ref
        if (onPageStatusUpdateRef.current) {
          onPageStatusUpdateRef.current(finalStatus);
        }

        return finalStatus;
      });
    }
  }, []); // No dependencies needed - uses refs

  // Setup websocket listener
  useEffect(() => {
    const socket = websocketService.getSocket();

    const attachListener = () => {
      if (isListenerAttached.current) return;
      websocketService.onUpdatePageProgress(handlePageProgressUpdate);
      isListenerAttached.current = true;
    };

    const detachListener = () => {
      if (isListenerAttached.current) {
        websocketService.offUpdatePageProgress(handlePageProgressUpdate);
        isListenerAttached.current = false;
      }
    };

    // Attach immediately if already connected
    if (websocketService.isSocketConnected()) {
      attachListener();
    }

    // Keep trying until connected (covers late socket connect)
    // Clear any existing interval first
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
    }
    
    retryIntervalRef.current = setInterval(() => {
      if (websocketService.isSocketConnected() && !isListenerAttached.current) {
        attachListener();
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      }
    }, 1500);

    // Also re-attach on reconnect events
    const onConnect = () => {
      if (!isListenerAttached.current) {
        attachListener();
      }
    };

    socket?.on('connect', onConnect);
    socket?.on('reconnect', onConnect);

    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      socket?.off('connect', onConnect);
      socket?.off('reconnect', onConnect);
      detachListener();
    };
  }, [handlePageProgressUpdate]);

  // Combined effect: Reset and fetch when studentId changes
  // This prevents race conditions between reset and fetch
  useEffect(() => {
    // Reset state immediately
    setPageStatus([]);
    setError(null);
    setIsLoading(false);

    // Fetch new data
    if (studentId) {
      // Use the stable fetchPageStatus function
      fetchPageStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]); // fetchPageStatus is stable (no deps), so we can safely omit it

  // Manual refresh function
  const refreshPageStatus = useCallback(() => {
    fetchPageStatus();
  }, [fetchPageStatus]);

  return {
    pageStatus,
    isLoading,
    error,
    refreshPageStatus,
  };
};
